import React, {useEffect, useState} from 'react';
import styled from 'styled-components';

interface InfiniteSliderProps<T> {
  items: T[];
  SlideComponent: React.ComponentType<{
    item: T;
  }>;
  durationMs?: number;
  className?: string;
}

export const InfiniteSlider = <T extends object>({
  items,
  SlideComponent,
  durationMs = 3000,
  className,
}: InfiniteSliderProps<T>) => {
  const [currentIndex, setCurrentIndex] = useState(0);

  useEffect(() => {
    const changeSlide = () => {
      const newIndex = currentIndex + 1;

      // eslint-disable-next-line local-rules/set-state-in-use-effect
      setCurrentIndex(newIndex < items.length ? newIndex : 0);
    };

    const slideInterval = window.setInterval(changeSlide, durationMs);

    return () => {
      window.clearInterval(slideInterval);
    };
  }, [items.length, currentIndex, durationMs]);

  return (
    <Container className={className}>
      {items.map((item, index) => {
        const isCurrentItem = currentIndex === index;
        const isPreviousItem = (index + 1) % items.length === currentIndex;

        return (
          <Slide
            key={index}
            // eslint-disable-next-line no-nested-ternary
            left={isCurrentItem ? '0' : isPreviousItem ? '-100%' : '100%'}
            animation={
              // eslint-disable-next-line no-nested-ternary
              isCurrentItem
                ? 'infinite-slider-bounce 0.4s ease-out'
                : isPreviousItem
                ? 'infinite-slider-slide 0.3s ease-out'
                : undefined
            }
            data-testid={`slide-${index}${isCurrentItem ? '-active' : ''}`}
          >
            <SlideComponent item={item} />
          </Slide>
        );
      })}
    </Container>
  );
};

const Container = styled.div`
  position: relative;
  height: 36px;
  overflow: hidden;

  @keyframes infinite-slider-bounce {
    0% {
      transform: translateX(100%);
    }
    30% {
      transform: translateX(-16px);
    }
    100% {
      transform: translateX(0);
    }
  }

  @keyframes infinite-slider-slide {
    from {
      opacity: 1;
      transform: translateX(0);
    }
    to {
      opacity: 0;
      transform: translateX(-100%);
    }
  }
`;

const Slide = styled.div<{
  left?: string;
  transition?: string;
  animation?: string;
}>`
  position: absolute;
  inset: 0;
  transform: translateX(${({left}) => left});
  animation: ${({animation}) => animation};
`;
